import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { useStayPacks } from "@/hooks/useStayPacks";
import { isStayPackActiveOnDate } from "../schedule/scheduleModel";

export { isStayPackActiveOnDate } from "../schedule/scheduleModel";

export function countPackIncompatibilities(
  petId: string | null | undefined,
  memberPetIds: string[],
  relationships: Array<{ pet_id: string; related_pet_id: string; relationship: string }>,
) {
  if (!petId) return 0;
  return relationships.filter((relationship) => relationship.relationship === "dislikes" && memberPetIds.includes(relationship.pet_id === petId ? relationship.related_pet_id : relationship.pet_id)).length;
}

export function useReservationPacks({ date, petId }: { date: string; petId?: string | null }) {
  const query = useStayPacks();
  const { data: customerId } = useCurrentClinicCustomerId();
  const relationships = useQuery({
    queryKey: ["reservation-pack-relationships", customerId, petId],
    queryFn: async () => {
      if (!customerId || !petId) return [] as Array<{ pet_id: string; related_pet_id: string; relationship: string }>;
      const { data, error } = await supabase
        .from("pet_relationships")
        .select("pet_id, related_pet_id, relationship")
        .eq("customer_id", customerId)
        .or(`pet_id.eq.${petId},related_pet_id.eq.${petId}`);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!customerId && !!petId,
  });
  const activePacks = useMemo(
    () => (query.data ?? [])
      .filter((pack) => isStayPackActiveOnDate(pack, date))
      .map((pack) => ({
        id: pack.id,
        name: pack.name,
        color: pack.color,
        memberCount: pack.members.length,
        includesPet: pack.members.some((member) => member.petId === petId),
        incompatibilityCount: countPackIncompatibilities(petId, pack.members.map((member) => member.petId), relationships.data ?? []),
      })),
    [date, petId, query.data, relationships.data],
  );
  return { ...query, activePacks, recommendedPackId: activePacks.find((pack) => pack.includesPet)?.id ?? activePacks[0]?.id ?? null };
}
